import { PayloadAction } from "@reduxjs/toolkit";
import { SagaIterator } from "redux-saga";
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeEvery
} from "redux-saga/effects";
import { pendingPollsSelector } from "./selectors";
import { startPolling, stopPolling } from "./slices";
import { Duration, StartPollingParams } from "./types";

const DEFAULT_POLLING_INTERVAL: Duration = 10 * 1000; // in milliseconds

function isPageVisible(): boolean {
  return typeof document === "undefined" || !document.hidden;
}

function* pollingWorkerSaga(
  id: string,
  actionType: string,
  params?: Record<string, unknown>,
  interval?: Duration
) {
  const POLLING_INTERVAL =
    interval && interval > 0 ? interval : DEFAULT_POLLING_INTERVAL;
  while (true) {
    if (isPageVisible()) {
      yield put({
        type: actionType,
        payload: {
          ...(params ?? {}),
          pollingId: id,
          interval
        }
      });
    }
    yield delay(POLLING_INTERVAL);
  }
}

export function* pollingWatcherSaga(action: PayloadAction<StartPollingParams>) {
  const { id, actionType, interval, params } = action.payload;

  yield race({
    task: call(pollingWorkerSaga, id, actionType, params, interval),
    /*
     * One id means one worker: a later startPolling with the same id replaces
     * this worker instead of running alongside it, so repeated starts cannot
     * multiply the request rate and the newest params always win.
     */
    cancel: take(
      (candidate: unknown) =>
        (stopPolling.match(candidate) || startPolling.match(candidate)) &&
        candidate.payload.id === id
    )
  });
}

export function* isPollStoppedSaga(
  action: PayloadAction<{ pollingId?: string }>
) {
  const pendingPolls: string[] = yield select(pendingPollsSelector);
  const pollingId = action.payload.pollingId;

  return Boolean(pollingId && !pendingPolls.includes(pollingId));
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([takeEvery(startPolling, pollingWatcherSaga)]);
}
